/**
 * Les échéances de facturation d'une version : **exactement le CA du Gantt, rien d'autre.**
 *
 * *Demande de Chris, 21/09/2026 : « dans le tableau de bord puis exploitation tu peux créer
 * onglet Facturation Prévisionnelle qui reprendra ces montants ; l'idée étant d'avoir en
 * permanence le montant facturable ».* Et, le même jour : *« on est bien d'accord que tu
 * positionnes les facturations par rapport à ce que nous avons dit pour les lignes de
 * chapitre ? »* — oui, et c'est tout l'objet de ce module.
 *
 * ## Une seule règle, deux écrans
 *
 * Le montant et la date sont ceux que la barre grise du planning affiche déjà depuis le
 * 16/08/2026 : **la vente des postes retenus du chapitre, posée au dernier jour qu'il
 * occupe, pastilles comprises**. Rien n'est recalculé autrement ici. C'est la raison d'être
 * du module : deux calculs de « quand cet argent rentre-t-il ? » finiraient par répondre deux
 * mois différents, et le tableau de bord contredirait le Gantt sans que rien ne dise lequel a
 * raison. Le planning en dépend aussi — il n'y a donc **pas** de copie côté écran.
 *
 * ## Le grain : la TÂCHE, à n'importe quelle profondeur — décision de Chris, 25/09/2026
 *
 * Le grain a changé deux fois le même jour, et c'est l'histoire qui explique la règle.
 *
 *  1. Jusqu'au 25/09 au matin, le montant vivait sur le **nœud racine** (le chapitre « 1 »).
 *  2. *« Je voudrais ne plus facturer sur le chapitre mais sur les sous-chapitres, c'est-à-dire
 *     dans l'exemple APO260098, pas sur 1 mais sur 1.1, 1.3… 1.9. »* Posé alors sur « l'enfant
 *     direct d'un chapitre racine » — or 1.1 à 1.9 sont des tâches et 2.1 un sous-chapitre, si
 *     bien que le chapitre 2 affichait un seul montant de 186 719,45 € sur 2.1.
 *  3. *« Sur le chapitre 2 il faudrait que la facturation apparaisse sur la ligne 2.1.1 »* —
 *     2.1.1 TERRASSEMENTS est une tâche. Les deux exemples disent la même chose : **le montant
 *     se pose sur chaque tâche, quelle que soit sa profondeur, et jamais sur un chapitre ni un
 *     sous-chapitre.** Confirmé par Chris le soir même.
 *
 * La tâche est le poste du devis, celui qui porte les lignes de ressources — et le grain d'une
 * situation de travaux. La règle ne dépend plus de la profondeur : un devis plat (APO250026),
 * une tâche sous un chapitre (1.1) ou quatre niveaux plus bas dans une copie restructurée, c'est
 * le même cas. Chaque échéance porte le **chapitre qui contient la tâche**, pour que le carnet
 * de facturation regroupe ses lignes (*« regroupement par chapitre »*, même décision).
 *
 * Cette liste **somme exactement au total de vente retenu de la version**, par construction :
 * chaque tâche retenue sous des chapitres retenus porte sa vente une fois. `totalDesEcheances`
 * le rend pour que l'écran le **montre** face au montant du marché, plutôt que de le supposer.
 *
 * ## Ce qui n'a pas de date n'en reçoit pas
 *
 * `jourRelatif` est nul quand le chapitre ne porte **aucune** barre ni pastille : rien n'y est
 * planifié, donc rien ne dit quand il se facture. Le tableau de bord en fait une ligne « non
 * planifié » plutôt que de le loger dans un mois — inventer une date ferait un carnet qui a
 * l'air juste. Même doctrine que la donnée manquante partout ailleurs dans ce produit.
 */
import { barresDuResume, lignesPlanifiees, periodesEnDemiJournees } from "./barre-grise.js";
import { dec, Decimal } from "./nombre.js";
import { DEMI_JOURNEES_PAR_JOUR, etendueEnDemiJournees, indexDemiJournee } from "./planning.js";
/**
 * La dernière demi-journée qu'un ensemble de tâches occupe — **pastilles comprises**.
 *
 * Les pastilles comptent ici alors qu'elles ne comptent pas dans la barre grise, et c'est
 * délibéré : la barre dit *combien de temps on travaille*, l'échéance dit *quand cet argent
 * rentre*. Un chapitre soldé par une dernière livraison est facturable ce jour-là même si
 * plus aucune barre ne le suit — et un chapitre dont rien n'est encore barré mais dont une
 * livraison est posée, l'état normal d'un chantier au début de sa planification, garderait
 * sinon un CA sans date.
 */
export function finOccupee(taches) {
    const desBarres = etendueEnDemiJournees(taches.flatMap(barresDuResume));
    let fin = desBarres?.fin ?? Number.NEGATIVE_INFINITY;
    const joursPleins = [
        ...taches.flatMap((t) => lignesPlanifiees(t).flatMap((r) => r.planning.livraisons)),
        ...taches.flatMap((t) => lignesPlanifiees(t).flatMap((r) => r.planning.transports)),
    ].map((p) => p.jourRelatif);
    for (const jour of joursPleins) {
        const soir = (jour - 1) * DEMI_JOURNEES_PAR_JOUR + 1;
        if (soir > fin)
            fin = soir;
    }
    return fin === Number.NEGATIVE_INFINITY ? null : fin;
}
/** Le jour relatif que porte une demi-journée. J1 occupe les index 0 et 1. */
export function jourDeLaDemiJournee(demi) {
    return Math.floor(demi / DEMI_JOURNEES_PAR_JOUR) + 1;
}
function echeanceUnique(tache, chapitre) {
    const fin = finOccupee([tache]);
    return {
        numero: tache.numero,
        designation: tache.designation,
        chapitre,
        prixVente: tache.prixVente,
        rang: 1,
        parts: 1,
        jourRelatif: fin === null ? null : jourDeLaDemiJournee(fin),
        finDemiJournee: fin,
    };
}
/**
 * Le poids de chaque période dans la répartition : **le déboursé qu'on y a planifié**.
 *
 * *Décision de Chris, 25/09/2026 : « au prorata du déboursé »* — une grosse journée pèse plus
 * qu'une demi-journée. Une barre de ligne pèse le coût de sa ligne au prorata de sa durée
 * parmi les barres de cette ligne ; hacher ne changeant ni heure ni euro, c'est exactement le
 * coût que la reprise représente.
 *
 * Repli sur la **durée** dans deux cas où le coût ne dit rien : une tâche de fournitures
 * pures, dont la barre est portée par la tâche elle-même et non par une ligne chiffrée, et un
 * coût total nul ou négatif (une moins-value) — des poids sans signe cohérent feraient des
 * parts absurdes.
 */
export function poidsDesPeriodes(tache, periodes) {
    const dans = (barre) => {
        const index = indexDemiJournee(barre);
        return periodes.findIndex((p) => index >= p.debut && index <= p.fin);
    };
    const parCout = periodes.map(() => dec(0));
    const parDuree = periodes.map(() => dec(0));
    let coutUtilisable = tache.planning.phases.length === 0;
    for (const phase of tache.planning.phases) {
        const i = dans(phase);
        if (i >= 0)
            parDuree[i] = parDuree[i].plus(phase.demiJournees);
    }
    for (const ressource of lignesPlanifiees(tache).filter((r) => r.impacteBarreGrise)) {
        const duree = ressource.planning.phases.reduce((t, p) => t + p.demiJournees, 0);
        if (duree === 0)
            continue;
        for (const phase of ressource.planning.phases) {
            const i = dans(phase);
            if (i < 0)
                continue;
            parDuree[i] = parDuree[i].plus(phase.demiJournees);
            parCout[i] = parCout[i].plus(dec(ressource.cout).times(phase.demiJournees).dividedBy(duree));
        }
    }
    if (parCout.some((c) => c.isNegative()) || parCout.reduce((t, c) => t.plus(c), dec(0)).lte(0)) {
        coutUtilisable = false;
    }
    return coutUtilisable ? parCout : parDuree;
}
/**
 * Répartir un montant selon des poids, **au centime et sans rien perdre** : chaque part est
 * tronquée au centime, et les centimes restants vont à la PREMIÈRE — la règle du hachage des
 * heures, qui donne le reste aux premières reprises. La somme vaut toujours le montant.
 */
export function repartirMontant(montant, poids) {
    const total = poids.reduce((t, p) => t.plus(p), dec(0));
    const parts = poids.map((p) => dec(montant).times(p).dividedBy(total).toDecimalPlaces(2, Decimal.ROUND_DOWN));
    const reste = dec(montant).minus(parts.reduce((t, p) => t.plus(p), dec(0)));
    parts[0] = parts[0].plus(reste);
    return parts.map((p) => p.toFixed(2));
}
/**
 * Les échéances d'une tâche : **une par période de travail** (Chris, 25/09/2026).
 *
 * Chaque période se facture à son dernier jour. Les pastilles n'ouvrent pas de période : une
 * livraison se facture avec le travail qui la suit, et celle qui vient après la dernière barre
 * repousse la dernière échéance à son jour, exactement comme `finOccupee` le fait pour la
 * tâche entière.
 */
export function echeancesDeLaTache(tache, chapitre = null) {
    const periodes = periodesEnDemiJournees(barresDuResume(tache));
    if (periodes.length <= 1)
        return [echeanceUnique(tache, chapitre)];
    const montants = repartirMontant(tache.prixVente, poidsDesPeriodes(tache, periodes));
    const finTache = finOccupee([tache]);
    return periodes.map((periode, i) => {
        const derniere = i === periodes.length - 1;
        const fin = derniere && finTache !== null ? Math.max(periode.fin, finTache) : periode.fin;
        return {
            numero: tache.numero,
            designation: tache.designation,
            chapitre,
            prixVente: montants[i],
            rang: i + 1,
            parts: periodes.length,
            jourRelatif: jourDeLaDemiJournee(fin),
            finDemiJournee: fin,
        };
    });
}
/**
 * Les échéances d'une version : chaque tâche retenue, sous des chapitres retenus, dans l'ordre
 * de l'arbre.
 *
 * Les postes écartés du chiffrage sont omis : une option qu'on ne fait pas ne se facture
 * pas, exactement comme elle n'entre ni dans le CA du Gantt ni dans le montant du marché.
 */
export function echeancesDeLaVersion(racines) {
    const echeances = [];
    const parcourir = (noeuds, chapitre) => {
        for (const noeud of noeuds) {
            if (!noeud.retenu)
                continue;
            if (noeud.nature === "ouvrage") {
                echeances.push(...echeancesDeLaTache(noeud, chapitre));
            }
            else {
                parcourir(noeud.enfants, { numero: noeud.numero, designation: noeud.designation });
            }
        }
    };
    parcourir(racines, null);
    return echeances;
}
/**
 * La somme des échéances, au centime — le premier terme du contrôle demandé par Chris le
 * 25/09/2026 : *« il faudra aussi avoir un contrôle que tous les montants correspondent bien
 * au montant total du marché de la contre-étude »*.
 *
 * Additionnée en `Decimal` et non en flottant : un contrôle qui annoncerait 0,01 € d'écart
 * sur un arrondi de somme ferait douter de montants justes.
 */
export function totalDesEcheances(echeances) {
    return echeances.reduce((total, e) => total.plus(dec(e.prixVente)), dec(0)).toFixed(2);
}
//# sourceMappingURL=facturation.js.map